// @flow

/**
 * Data Display Tag Component
 * Bridge between the <data-display> custom tag in markdown and the
 * DataDisplayRenderer. Looks up the display definition from context
 * and delegates rendering.
 */

import React from 'react';
import { useDataDisplays } from './DataDisplayContext';
import DataDisplayRenderer from './DataDisplayRenderer';
import DataDisplayErrorBoundary from './DataDisplayErrorBoundary';
import DataDisplayPlaceholder from './DataDisplayPlaceholder';

type Props = {
  id?: string,
  children?: React$Node,
};

const DataDisplayTag = ({ id }: Props) => {
  const dataDisplays = useDataDisplays();

  if (!id) {
    return <DataDisplayPlaceholder message="Display reference missing" />;
  }

  // rehype-sanitize prefixes "user-content-" to id attributes (GitHub schema behaviour).
  // Strip it to match the original displayId from the tool.
  const displayId = id.startsWith('user-content-') ? id.slice(13) : id;
  const definition = dataDisplays[displayId];

  if (!definition) {
    return <DataDisplayPlaceholder message="Visual component data unavailable" />;
  }

  return (
    <DataDisplayErrorBoundary displayId={displayId}>
      <DataDisplayRenderer definition={definition} displayId={displayId} />
    </DataDisplayErrorBoundary>
  );
};

export default DataDisplayTag;
